// Drops the `## Relations` heading and its body from a wiki page's
// markdown so the SPA can render the article without duplicating the
// content that `WikiPageRelations` already surfaces above it. Pure
// string transform — disk content is untouched.
//
// The matched span is the heading line plus everything up to (but not
// including) the next H2 heading or end of string. Mirrors the
// Ruby-side block boundaries in `app/services/wiki/relations_parser.rb`
// (`HEADING` + `BLOCK_PATTERN`) and the bullet shape that
// `app/services/wiki/relations_block_writer.rb` emits. If either
// boundary contract changes on the Ruby side, update the regexes
// below and extend the cross-side fixture test in this module's spec.
//
// `HEADING_LITERAL` is the single source of truth for the heading
// text; both the start anchor and the block terminator are derived
// from it so a future rename (e.g. `## Relations` → `## Related`)
// is a one-line edit, parallel to Ruby's `RelationsParser::HEADING`
// constant.

const HEADING_LITERAL = '## Relations'

const BLOCK_START = new RegExp(`^${HEADING_LITERAL}[ \\t]*$`, 'm')
// The block ends at the first subsequent H2 line. Matches Ruby's
// `RelationsParser::BLOCK_PATTERN` terminator (`^##[ \t]+\S`) so the
// permissive-whitespace grammar agrees across sides — `## Notes`,
// `##\tNotes`, and `##   Notes` all terminate the block. The strip
// walks past the heading's trailing newline (`searchFrom` below)
// before applying this regex so the start anchor can't self-match.
const BLOCK_TERMINATOR = /^##[ \t]+\S/m

export function stripRelationsBlock(content: string): string {
  const headingMatch = BLOCK_START.exec(content)
  if (!headingMatch) return content

  // Step past the heading's trailing newline before searching for the
  // terminator so the heading can't match itself as its own terminator.
  const searchFrom = headingMatch.index + headingMatch[0].length + 1
  const terminator = BLOCK_TERMINATOR.exec(content.slice(searchFrom))
  const blockEnd = terminator ? searchFrom + terminator.index : content.length

  const before = content.slice(0, headingMatch.index).replace(/\n+$/, '')
  const after = content.slice(blockEnd).replace(/^\n+/, '')

  if (!before && !after) return ''
  if (!before) return after
  if (!after) return `${before}\n`
  return `${before}\n\n${after}`
}
